import {
  companyDb,
  ensureDeviceId,
  getIdMaps,
  isLocalFirstMode,
  newOfflineId,
  resolveServerId,
} from './companyContext';
import { stockKey, type CatalogCustomer, type CatalogItem, type CatalogSupplier } from './db';
import { enqueueSync } from './queue';
import { useSyncStore } from '@/store/syncStore';
import { useAuthStore } from '@/store/authStore';
import { useDesktopSessionStore } from '@/desktop/desktopSessionStore';
import { evaluateCart, type PromotionCampaign } from '@/utils/promotions';

function triggerAutoSync() {
  // Never kick sync while offline — ping hangs the UX and isn't useful.
  if (!navigator.onLine || !useSyncStore.getState().isServerReachable) return;
  void import('./syncWorker').then((m) => m.maybeAutoSync());
}

async function nextInvoiceNo(): Promise<string> {
  const settings = await companyDb().catalog_settings.get('settings');
  const prefix =
    ((settings?.value as { offline_invoice_prefix?: string } | undefined)?.offline_invoice_prefix) ||
    'OFF';
  const counter = ((await companyDb().meta.get('invoiceCounter'))?.value as number) || 0;
  const next = counter + 1;
  await companyDb().meta.put({ key: 'invoiceCounter', value: next });
  const date = new Date();
  const ymd = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`;
  return `${prefix}-${ymd}-${String(next).padStart(5, '0')}`;
}

export async function listLocalItems(opts?: {
  search?: string;
  item_type?: string;
}): Promise<CatalogItem[]> {
  let items = await companyDb().catalog_items.toArray();
  if (opts?.item_type) items = items.filter((i) => i.item_type === opts.item_type);
  if (opts?.search) {
    const q = opts.search.toLowerCase();
    items = items.filter(
      (i) =>
        i.item_name?.toLowerCase().includes(q) ||
        i.item_code?.toLowerCase().includes(q) ||
        i.barcode?.toLowerCase().includes(q),
    );
  }
  // Attach live local stock
  const stockRows = await companyDb().local_stock.toArray();
  const stockMap = new Map(stockRows.map((s) => [s.id, s.qty]));
  return items.map((item) => ({
    ...item,
    stock_quantity: stockMap.get(stockKey(item.name)) ?? item.stock_quantity ?? 0,
    variants: (item.variants || []).map((v) => {
      const name = String((v as { name?: string }).name || '');
      return {
        ...v,
        stock_quantity: stockMap.get(stockKey(item.name, name)) ?? (v as { stock_quantity?: number }).stock_quantity ?? 0,
      };
    }),
  }));
}

export async function getLocalItem(name: string): Promise<CatalogItem | undefined> {
  const items = await listLocalItems();
  return items.find((i) => i.name === name);
}

export async function saveLocalItem(
  data: Partial<CatalogItem> & { item_name: string; item_type: 'Product' | 'Service' },
  isUpdate = false,
): Promise<CatalogItem> {
  if (!isLocalFirstMode()) throw new Error('Local item save only in local-first modes');
  const db = companyDb();
  const clientOfflineId = isUpdate && data.name?.startsWith('local_')
    ? data.name
    : isUpdate && data.name
      ? `upd_item_${data.name}_${Date.now()}`
      : newOfflineId('local_item');

  const name = isUpdate && data.name ? data.name : clientOfflineId.replace(/^local_item_/, 'local_');
  const item: CatalogItem = {
    name,
    item_code: data.item_code,
    item_name: data.item_name,
    image: data.image,
    category: data.category,
    brand: data.brand,
    custom_model: data.custom_model,
    item_type: data.item_type,
    sale_price: Number(data.sale_price) || 0,
    wholesale_price: Number(data.wholesale_price) || 0,
    reseller_price: Number(data.reseller_price) || 0,
    minimum_price: Number(data.minimum_price) || 0,
    cost_price: Number(data.cost_price) || 0,
    tax_rate: Number(data.tax_rate) || 0,
    stock_quantity: Number(data.stock_quantity) || 0,
    barcode: data.barcode,
    unit_of_measurement: data.unit_of_measurement || 'Unit/Pcs',
    has_variants: Boolean(data.has_variants),
    is_active: data.is_active !== false,
    allow_fractional_qty: data.allow_fractional_qty,
    maintain_stock: data.maintain_stock,
    custom_fields_data: data.custom_fields_data,
    variants: data.variants,
    isLocal: name.startsWith('local_'),
  };
  await db.catalog_items.put(item);
  if (item.item_type === 'Product' && !isUpdate) {
    await db.local_stock.put({ id: stockKey(item.name), item: item.name, qty: item.stock_quantity || 0 });
  }

  const deviceId = await ensureDeviceId();
  const payload = {
    ...item,
    client_offline_id: clientOfflineId,
    offline_device_id: deviceId,
    ...(isUpdate && data.name && !data.name.startsWith('local_') ? { name: data.name } : {}),
    ...(isUpdate ? { _action: 'update' } : {}),
  };
  await enqueueSync(isUpdate ? 'update_item' : 'create_item', clientOfflineId, payload as Record<string, unknown>);
  triggerAutoSync();
  return item;
}

/** Patch sale/wholesale/min prices on synced catalog rows after bulk server update. */
export async function patchLocalItemPrices(
  updates: Array<{
    name: string;
    variant?: string;
    sale_price?: number;
    wholesale_price?: number;
    reseller_price?: number;
    minimum_price?: number;
  }>,
): Promise<void> {
  const db = companyDb();
  for (const u of updates) {
    const existing = await db.catalog_items.get(u.name);
    if (!existing) continue;
    if (u.variant && Array.isArray(existing.variants)) {
      const variants = existing.variants.map((raw) => {
        const v = raw as { name?: string; price?: number; wholesale_price?: number; reseller_price?: number; minimum_price?: number };
        if (String(v.name || '') !== u.variant) return raw;
        return {
          ...v,
          ...(u.sale_price != null ? { price: u.sale_price } : {}),
          ...(u.wholesale_price != null ? { wholesale_price: u.wholesale_price } : {}),
          ...(u.reseller_price != null ? { reseller_price: u.reseller_price } : {}),
          ...(u.minimum_price != null ? { minimum_price: u.minimum_price } : {}),
        };
      });
      await db.catalog_items.update(u.name, { variants });
    } else {
      await db.catalog_items.update(u.name, {
        ...(u.sale_price != null ? { sale_price: u.sale_price } : {}),
        ...(u.wholesale_price != null ? { wholesale_price: u.wholesale_price } : {}),
        ...(u.reseller_price != null ? { reseller_price: u.reseller_price } : {}),
        ...(u.minimum_price != null ? { minimum_price: u.minimum_price } : {}),
      });
    }
  }
}

export async function listLocalCustomers(search?: string): Promise<CatalogCustomer[]> {
  let rows = await companyDb().catalog_customers.toArray();
  if (search) {
    const q = search.toLowerCase();
    rows = rows.filter(
      (c) =>
        c.customer_name?.toLowerCase().includes(q) ||
        (c.mobile || '').toLowerCase().includes(q),
    );
  }
  return rows;
}

export type LocalCustomerWithStats = CatalogCustomer & {
  total_sale: number;
  paid_amount: number;
  outstanding_balance: number;
};

function partyMatches(saleCustomer: string, party: string, idMaps: Record<string, string>): boolean {
  if (!saleCustomer || !party) return false;
  if (saleCustomer === party) return true;
  if (idMaps[saleCustomer] === party) return true;
  if (idMaps[party] === saleCustomer) return true;
  return false;
}

/** Customer cards: totals from local_sales (not stale catalog snapshot). */
export async function listLocalCustomersWithStats(search?: string): Promise<LocalCustomerWithStats[]> {
  const [rows, sales, idMaps] = await Promise.all([
    listLocalCustomers(search),
    companyDb().local_sales.toArray(),
    getIdMaps(),
  ]);

  const stats = new Map<string, { sale: number; paid: number }>();
  const bump = (key: string, sale: number, paid: number) => {
    if (!key) return;
    const cur = stats.get(key) || { sale: 0, paid: 0 };
    cur.sale += sale;
    cur.paid += paid;
    stats.set(key, cur);
  };

  for (const s of sales) {
    const net = Number(s.net_total) || 0;
    const paid = Number(s.paid_amount) || 0;
    // Prefer server id when known so cards keyed by CS-xxx pick up offline sales.
    bump(idMaps[s.customer] || s.customer, net, paid);
  }

  return rows.map((c) => {
    const canonical = idMaps[c.name] || c.name;
    const found = stats.get(c.name) || stats.get(canonical) || { sale: 0, paid: 0 };
    const total_sale = found.sale;
    const paid_amount = found.paid;
    return {
      ...c,
      total_sale,
      paid_amount,
      outstanding_balance: total_sale - paid_amount,
    };
  });
}

export async function listLocalCustomerInvoices(opts: {
  customer: string;
  from_date?: string;
  to_date?: string;
  status?: string;
}) {
  const idMaps = await getIdMaps();
  let sales = await companyDb().local_sales.toArray();
  sales = sales.filter((s) => partyMatches(s.customer, opts.customer, idMaps));
  if (opts.from_date || opts.to_date) {
    sales = sales.filter((s) => {
      const d = s.createdAt.slice(0, 10);
      if (opts.from_date && d < opts.from_date) return false;
      if (opts.to_date && d > opts.to_date) return false;
      return true;
    });
  }
  if (opts.status === 'Unpaid') {
    sales = sales.filter((s) => s.status === 'Unpaid' || s.status === 'Partially Paid');
  } else if (opts.status === 'Paid') {
    sales = sales.filter((s) => s.status === 'Paid');
  }
  sales.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  return sales.map((s) => {
    const net = Number(s.net_total) || 0;
    const paid = Number(s.paid_amount) || 0;
    return {
      name: s.serverInvoiceName || s.offlineInvoiceNo,
      invoice_date: s.createdAt.slice(0, 10),
      status: s.status,
      net_total: net,
      paid_amount: paid,
      balance_amount: Math.max(0, net - paid),
      write_off_amount: Number(s.write_off_amount) || 0,
      notes: s.notes || '',
      payment_method: (s.payments?.[0] as { payment_method?: string } | undefined)?.payment_method || 'Cash',
      payments: (s.payments || []).map((p, idx) => ({
        name: `PAY-${idx + 1}`,
        amount: Number(p.amount) || 0,
        payment_method: String(p.payment_method || 'Cash'),
        posting_date: s.createdAt.slice(0, 10),
        reference_no: String(p.reference_no || ''),
      })),
      offline: true,
    };
  });
}

export async function getLocalCustomerPartyLedger(opts: {
  customer: string;
  customerName?: string;
  from_date: string;
  to_date: string;
}) {
  const idMaps = await getIdMaps();
  const allSales = (await companyDb().local_sales.toArray()).filter((s) =>
    partyMatches(s.customer, opts.customer, idMaps),
  );

  let opening = 0;
  for (const s of allSales) {
    const d = s.createdAt.slice(0, 10);
    if (d < opts.from_date) {
      opening += (Number(s.net_total) || 0) - (Number(s.paid_amount) || 0);
    }
  }

  const sales = allSales
    .filter((s) => {
      const d = s.createdAt.slice(0, 10);
      return d >= opts.from_date && d <= opts.to_date;
    })
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));

  let balance = opening;
  const entries = sales.map((s) => {
    const debit = Number(s.net_total) || 0;
    const credit = Number(s.paid_amount) || 0;
    balance += debit - credit;
    return {
      posting_date: s.createdAt.slice(0, 10),
      invoice_no: s.serverInvoiceName || s.offlineInvoiceNo,
      payment_no: credit > 0 ? 'Payment' : '',
      description: 'POS Sale',
      debit,
      credit,
      balance,
    };
  });

  return {
    party_name: opts.customerName || opts.customer,
    opening_balance: opening,
    closing_balance: balance,
    balance_label: balance >= 0 ? 'Receivable' : 'Advance',
    entries,
    from_date: opts.from_date,
    to_date: opts.to_date,
    source: 'local' as const,
  };
}

export async function saveLocalCustomer(
  data: Partial<CatalogCustomer> & { customer_name: string },
  isUpdate = false,
): Promise<CatalogCustomer> {
  if (!isLocalFirstMode()) throw new Error('Local customer save only in local-first modes');
  const clientOfflineId = isUpdate && data.name?.startsWith('local_')
    ? data.name
    : isUpdate && data.name
      ? `upd_cust_${data.name}_${Date.now()}`
      : newOfflineId('local_cust');
  const name = isUpdate && data.name ? data.name : clientOfflineId.replace(/^local_cust_/, 'local_');
  const customer: CatalogCustomer = {
    name,
    customer_name: data.customer_name,
    mobile: data.mobile,
    email: data.email,
    city: data.city,
    address: data.address,
    is_walk_in: data.is_walk_in,
    outstanding_balance: data.outstanding_balance || 0,
    is_default: data.is_default,
    customer_type: data.customer_type || 'Retailer',
    creation: data.creation || new Date().toISOString(),
    isLocal: name.startsWith('local_'),
  };
  await companyDb().catalog_customers.put(customer);
  const deviceId = await ensureDeviceId();
  await enqueueSync(
    isUpdate ? 'update_customer' : 'create_customer',
    clientOfflineId,
    {
      ...customer,
      client_offline_id: clientOfflineId,
      offline_device_id: deviceId,
      ...(isUpdate && data.name && !data.name.startsWith('local_') ? { name: data.name } : {}),
      _action: isUpdate ? 'update' : 'create',
    },
  );
  triggerAutoSync();
  return customer;
}

export async function listLocalSuppliers(search?: string): Promise<CatalogSupplier[]> {
  let rows = await companyDb().catalog_suppliers.toArray();
  if (search) {
    const q = search.toLowerCase();
    rows = rows.filter(
      (s) =>
        s.supplier_name?.toLowerCase().includes(q) ||
        (s.mobile || '').toLowerCase().includes(q),
    );
  }
  return rows;
}

export async function saveLocalSupplier(
  data: Partial<CatalogSupplier> & { supplier_name: string },
  isUpdate = false,
): Promise<CatalogSupplier> {
  if (!isLocalFirstMode()) throw new Error('Local supplier save only in local-first modes');
  const clientOfflineId = isUpdate && data.name?.startsWith('local_')
    ? data.name
    : isUpdate && data.name
      ? `upd_sup_${data.name}_${Date.now()}`
      : newOfflineId('local_sup');
  const name = isUpdate && data.name ? data.name : clientOfflineId.replace(/^local_sup_/, 'local_');
  const supplier: CatalogSupplier = {
    name,
    supplier_name: data.supplier_name,
    mobile: data.mobile,
    email: data.email,
    city: data.city,
    address: data.address,
    isLocal: name.startsWith('local_'),
  };
  await companyDb().catalog_suppliers.put(supplier);
  const deviceId = await ensureDeviceId();
  await enqueueSync(
    isUpdate ? 'update_supplier' : 'create_supplier',
    clientOfflineId,
    {
      ...supplier,
      client_offline_id: clientOfflineId,
      offline_device_id: deviceId,
      ...(isUpdate && data.name && !data.name.startsWith('local_') ? { name: data.name } : {}),
    },
  );
  triggerAutoSync();
  return supplier;
}

export async function adjustLocalStock(opts: {
  type: 'stock_in' | 'stock_adjust' | 'stock_out';
  item: string;
  variant?: string;
  quantity: number;
  unit_price?: number;
  supplier?: string;
  reason?: string;
  transaction_date?: string;
}): Promise<string> {
  if (!isLocalFirstMode()) throw new Error('Local stock only in local-first modes');
  const qty = Math.abs(Number(opts.quantity) || 0);
  if (qty <= 0) throw new Error('Quantity must be greater than zero');

  const clientOfflineId = newOfflineId('stock');
  const key = stockKey(opts.item, opts.variant);
  const db = companyDb();
  const existing = await db.local_stock.get(key);
  const current = existing?.qty || 0;
  let next = current;
  if (opts.type === 'stock_in') next = current + qty;
  else if (opts.type === 'stock_out') next = current - qty;
  else next = qty; // adjust = set absolute

  await db.local_stock.put({ id: key, item: opts.item, variant: opts.variant, qty: next });
  const item = await db.catalog_items.get(opts.item);
  if (item && !opts.variant) {
    await db.catalog_items.update(opts.item, { stock_quantity: next });
  }

  await db.local_stock_movements.put({
    clientOfflineId,
    type: opts.type,
    item: opts.item,
    variant: opts.variant,
    quantity: opts.type === 'stock_adjust' ? next : qty,
    unit_price: opts.unit_price,
    reason: opts.reason,
    createdAt: new Date().toISOString(),
  });

  const deviceId = await ensureDeviceId();
  const payload: Record<string, unknown> = {
    item: opts.item,
    variant: opts.variant,
    quantity: opts.type === 'stock_adjust' ? Math.abs(next - current) || qty : qty,
    unit_price: opts.unit_price,
    supplier: opts.supplier,
    reason: opts.reason,
    transaction_date: opts.transaction_date || new Date().toISOString().slice(0, 10),
    client_offline_id: clientOfflineId,
    offline_device_id: deviceId,
    stock_out: opts.type === 'stock_out' ? opts.reason || 'Damage' : undefined,
    // For adjust: positive delta = stock_in, negative = stock_out
    _adjust_delta: opts.type === 'stock_adjust' ? next - current : undefined,
    _adjust_target: opts.type === 'stock_adjust' ? next : undefined,
  };
  await enqueueSync(opts.type, clientOfflineId, payload);
  triggerAutoSync();
  return clientOfflineId;
}

export async function getLocalStock(item: string, variant?: string): Promise<number> {
  const row = await companyDb().local_stock.get(stockKey(item, variant));
  if (row) return Number(row.qty) || 0;
  // Bootstrap may not have written local_stock yet — fall back to catalog qty.
  const cat = await companyDb().catalog_items.get(item);
  if (!cat) return 0;
  if (variant && Array.isArray(cat.variants)) {
    const v = cat.variants.find((x) => String((x as { name?: string }).name || '') === variant) as
      | { stock_quantity?: number }
      | undefined;
    return Number(v?.stock_quantity) || 0;
  }
  return Number(cat.stock_quantity) || 0;
}

/** Local cost / minimum price without a network call. */
export async function getLocalItemMinRate(item: string, variant?: string): Promise<number> {
  const cat = await companyDb().catalog_items.get(item);
  if (!cat) return 0;
  if (variant && Array.isArray(cat.variants)) {
    const v = cat.variants.find((x) => String((x as { name?: string }).name || '') === variant) as
      | { minimum_price?: number; cost_price?: number }
      | undefined;
    return Number(v?.minimum_price || v?.cost_price) || 0;
  }
  return Number(cat.minimum_price || cat.cost_price) || 0;
}

export type CompleteSaleInput = {
  customer: string;
  customerName?: string;
  items: Array<Record<string, unknown>>;
  discount_amount?: number;
  tax_amount?: number;
  write_off_amount?: number;
  payments: Array<Record<string, unknown>>;
  notes?: string;
  pos_shift?: string;
  prices_include_tax?: boolean;
  manager_approver?: string;
  sales_person?: string;
};

export async function completeLocalSale(input: CompleteSaleInput): Promise<{ name: string; offline: true }> {
  // Allow local sale whenever we are in local-first OR forced offline complete.
  // Do not throw solely on mode — caller may force this path when server is unreachable.

  const settings = await companyDb().catalog_settings.get('settings');
  const s = (settings?.value || {}) as {
    allow_sell_out_of_stock_item?: boolean;
    offline_sync_allow_negative_stock?: boolean;
    prices_include_tax?: boolean;
  };
  const unreachable = !navigator.onLine || !useSyncStore.getState().isServerReachable;
  // Offline checkout must not block on stock — cart already gated when items were added.
  // Also honor POS allow-OOS / offline negative-stock settings.
  const allowNeg =
    unreachable ||
    Boolean(s.allow_sell_out_of_stock_item) ||
    s.offline_sync_allow_negative_stock !== false; // default allow when unset

  const db = companyDb();
  if (!allowNeg) {
    for (const line of input.items) {
      if (line.item_type === 'Service') continue;
      const itemName = String(line.item || '');
      const variant = line.variant ? String(line.variant) : undefined;
      const qty = Number(line.quantity) || 0;
      const stock = await getLocalStock(itemName, variant);
      if (stock < qty) {
        throw new Error(`Insufficient stock for ${String(line.item_name || itemName)}`);
      }
    }
  }

  const clientOfflineId = newOfflineId('sale');
  const offlineInvoiceNo = await nextInvoiceNo();
  const deviceId = await ensureDeviceId();
  const createdAt = new Date().toISOString();
  const mode = useSyncStore.getState().posSyncMode;
  const offlineSource =
    mode === 'Offline Always Manual Sync' || mode === 'Local First Auto Sync'
      ? mode
      : 'Local First Auto Sync';

  const promoRow = await db.catalog_settings.get('promotions');
  const promotions = (promoRow?.value || []) as PromotionCampaign[];
  const customerRow = await db.catalog_customers.get(input.customer);
  const customerType = customerRow?.customer_type || 'Retailer';
  const promoItems = [];
  for (const line of input.items || []) {
    let category = String(line.category || '');
    if (!category && line.item) {
      const catItem = await db.catalog_items.get(String(line.item));
      category = String(catItem?.category || '');
    }
    promoItems.push({
      item: String(line.item || ''),
      category,
      quantity: Number(line.quantity) || 0,
      rate: Number(line.rate) || 0,
      amount: Number(line.amount) || 0,
      variant: line.variant ? String(line.variant) : undefined,
      minimum_price: Number(line.minimum_price) || 0,
    });
  }
  const promoEval = evaluateCart(promoItems, customerType, promotions);
  const chargedItems = (input.items || []).map((line, idx) => {
    const promoLine = promoEval.lines[idx];
    const qty = Number(line.quantity) || 0;
    const charged = promoLine?.charged_amount ?? (Number(line.amount) || 0);
    return {
      ...line,
      original_rate: Number(line.rate) || 0,
      amount: charged,
      rate: qty ? charged / qty : Number(line.rate) || 0,
      promo_amount: promoLine?.promo_amount || 0,
      promo_qty: promoLine?.promo_qty || 0,
      promo_campaign: promoLine?.promo_campaign || '',
    };
  });

  let itemTotal = 0;
  for (const line of chargedItems) {
    itemTotal += Number(line.amount) || Number(line.rate) * Number(line.quantity) || 0;
  }
  const discount = Number(input.discount_amount) || 0;
  const tax = Number(input.tax_amount) || 0;
  const writeOff = Number(input.write_off_amount) || 0;
  const pricesIncludeTax = Boolean(input.prices_include_tax ?? s.prices_include_tax);
  const netTotal = pricesIncludeTax
    ? itemTotal - discount - writeOff
    : itemTotal - discount + tax - writeOff;
  const paid = (input.payments || []).reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const status = paid <= 0 ? 'Unpaid' : paid + 0.01 >= netTotal ? 'Paid' : 'Partially Paid';

  // Deduct stock (use catalog-aware balance so missing local_stock rows don't jump from 0)
  for (const line of input.items) {
    if (line.item_type === 'Service') continue;
    const itemName = String(line.item || '');
    const variant = line.variant ? String(line.variant) : undefined;
    const qty = Number(line.quantity) || 0;
    const key = stockKey(itemName, variant);
    const current = await getLocalStock(itemName, variant);
    const next = current - qty;
    await db.local_stock.put({ id: key, item: itemName, variant, qty: next });
  }

  await db.local_sales.put({
    clientOfflineId,
    offlineInvoiceNo,
    createdAt,
    customer: input.customer,
    customerName: input.customerName,
    items: chargedItems,
    discount_amount: discount,
    promo_total: promoEval.total_saving,
    promo_campaigns: promoEval.campaigns.join(', '),
    tax_amount: tax,
    write_off_amount: writeOff,
    payments: input.payments,
    notes: input.notes,
    net_total: netTotal,
    paid_amount: paid,
    status,
    offlineSource,
  });

  const invoicePayload = {
    customer: input.customer,
    items: input.items,
    discount_amount: discount,
    tax_amount: tax,
    write_off_amount: writeOff,
    payments: input.payments,
    notes: input.notes,
    pos_shift: input.pos_shift,
    client_offline_id: clientOfflineId,
    offline_device_id: deviceId,
    offline_invoice_no: offlineInvoiceNo,
    is_offline_sync: 1,
    offline_source: offlineSource,
    offline_sale_datetime: createdAt,
  };

  await enqueueSync('sale', clientOfflineId, { invoice: invoicePayload }, offlineSource);
  triggerAutoSync();
  return { name: offlineInvoiceNo, offline: true };
}

/** Build receipt payload from a local offline sale (no server call). */
export async function getLocalReceiptData(invoiceRef: string): Promise<Record<string, unknown> | null> {
  const sale = await companyDb().local_sales
    .filter(
      (s) =>
        s.offlineInvoiceNo === invoiceRef ||
        s.clientOfflineId === invoiceRef ||
        s.serverInvoiceName === invoiceRef,
    )
    .first();
  if (!sale) return null;

  let item_total = 0;
  let service_total = 0;
  const items = (sale.items || []).map((line) => {
    const amount = Number(line.amount) || Number(line.rate) * Number(line.quantity) || 0;
    if (line.item_type === 'Service') service_total += amount;
    else item_total += amount;
    return {
      item_code: line.item_code || line.item || '',
      item_name: String(line.item_name || line.item || ''),
      item_type: String(line.item_type || 'Product'),
      quantity: Number(line.quantity) || 0,
      rate: Number(line.rate) || 0,
      amount,
      original_rate: Number(line.original_rate) || Number(line.rate) || 0,
      promo_amount: Number(line.promo_amount) || 0,
      variant_name: String(line.variant_name || ''),
    };
  });

  const companyRow = await companyDb().catalog_settings.get('company');
  const company = (companyRow?.value || {}) as {
    name?: string;
    company_name?: string;
    address?: string;
    mobile?: string;
    phone?: string;
    email?: string;
    logo?: string;
  };
  const authCompany = useAuthStore.getState().company;
  const authUser = useAuthStore.getState().user;
  const desktopProfile = useDesktopSessionStore.getState().activeProfile;

  const paid = sale.paid_amount || 0;
  const net = sale.net_total || 0;
  // Prefer local offline number until sync assigns a server name — avoids stale server names on receipt.
  const displayName = sale.serverInvoiceName || sale.offlineInvoiceNo;
  return {
    name: displayName,
    offline_invoice_no: sale.offlineInvoiceNo,
    customer_name: sale.customerName || sale.customer || 'Walk in',
    cashier: authUser?.name || '',
    cashier_name:
      desktopProfile?.displayName ||
      authUser?.full_name ||
      authUser?.name ||
      'Offline POS',
    company_name: company.name || company.company_name || authCompany?.name || '',
    company_address: company.address || '',
    company_phone: company.mobile || company.phone || '',
    company_email: company.email || '',
    company_logo: company.logo || '',
    invoice_date: sale.createdAt,
    creation: sale.createdAt,
    status: sale.status,
    item_total,
    service_total,
    discount_amount: sale.discount_amount || 0,
    promo_total: sale.promo_total || 0,
    promo_campaigns: sale.promo_campaigns || '',
    write_off_amount: sale.write_off_amount || 0,
    tax_amount: sale.tax_amount || 0,
    net_total: net,
    paid_amount: paid,
    balance_amount: Math.max(0, net - paid),
    notes: sale.notes || '',
    items,
    payments: (sale.payments || []).map((p) => ({
      payment_method: p.payment_method || 'Cash',
      bank_name: p.bank_name || '',
      account: p.account || '',
      reference_no: p.reference_no || '',
      amount: Number(p.amount) || 0,
    })),
    offline: !sale.serverInvoiceName,
  };
}

export async function saveLocalExpense(data: {
  amount: number;
  category?: string;
  payment_method?: string;
  cash_pocket?: string;
  bank_account?: string;
  remarks?: string;
  posting_date?: string;
}): Promise<string> {
  if (!isLocalFirstMode()) throw new Error('Local expense only in local-first modes');
  const clientOfflineId = newOfflineId('exp');
  const deviceId = await ensureDeviceId();
  const createdAt = data.posting_date || new Date().toISOString();
  await companyDb().local_expenses.put({
    clientOfflineId,
    createdAt,
    amount: Number(data.amount) || 0,
    category: data.category,
    payment_method: data.payment_method,
    cash_pocket: data.cash_pocket,
    bank_account: data.bank_account,
    remarks: data.remarks,
  });
  await enqueueSync('expense', clientOfflineId, {
    ...data,
    amount: Number(data.amount) || 0,
    client_offline_id: clientOfflineId,
    offline_device_id: deviceId,
  });
  triggerAutoSync();
  return clientOfflineId;
}

export async function listLocalExpenses(): Promise<
  Array<{
    clientOfflineId: string;
    createdAt: string;
    amount: number;
    category?: string;
    payment_method?: string;
    cash_pocket?: string;
    bank_account?: string;
    remarks?: string;
    serverName?: string;
    recorded_by?: string;
    recorded_by_name?: string;
    status?: string;
  }>
> {
  return companyDb().local_expenses.orderBy('createdAt').reverse().toArray();
}

/** Cash pockets from catalog settings (works offline). */
export async function listLocalCashPockets(): Promise<
  Array<{ name: string; pocket_name?: string; current_balance?: number; is_default?: number }>
> {
  const row = await companyDb().catalog_settings.get('cash_pockets');
  const local = (row?.value as Array<Record<string, unknown>>) || [];
  return local.map((p) => ({
    name: String(p.name || ''),
    pocket_name: p.pocket_name ? String(p.pocket_name) : undefined,
    current_balance: Number(p.current_balance) || 0,
    is_default: Number(p.is_default) || 0,
  }));
}

export async function listLocalBankAccounts(): Promise<
  Array<{
    name: string;
    bank_name?: string;
    account_title?: string;
    current_balance?: number;
    is_default?: number;
  }>
> {
  const row = await companyDb().catalog_settings.get('bank_accounts');
  const local = (row?.value as Array<Record<string, unknown>>) || [];
  return local.map((b) => ({
    name: String(b.name || ''),
    bank_name: b.bank_name ? String(b.bank_name) : undefined,
    account_title: b.account_title ? String(b.account_title) : undefined,
    current_balance: Number(b.current_balance) || 0,
    is_default: Number(b.is_default) || 0,
  }));
}

export async function getLocalSupplierPartyLedger(opts: {
  supplier: string;
  supplierName?: string;
  from_date: string;
  to_date: string;
}) {
  const idMaps = await getIdMaps();
  const all = (await companyDb().local_purchases.toArray()).filter((p) =>
    partyMatches(p.supplier, opts.supplier, idMaps),
  );

  let opening = 0;
  for (const p of all) {
    const d = p.createdAt.slice(0, 10);
    if (d < opts.from_date) {
      opening += (Number(p.net_total) || 0) - (Number(p.paid_amount) || 0);
    }
  }

  const purchases = all
    .filter((p) => {
      const d = p.createdAt.slice(0, 10);
      return d >= opts.from_date && d <= opts.to_date;
    })
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));

  let balance = opening;
  const entries = purchases.map((p) => {
    const credit = Number(p.net_total) || 0;
    const debit = Number(p.paid_amount) || 0;
    balance += credit - debit;
    return {
      posting_date: p.createdAt.slice(0, 10),
      invoice_no: p.serverName || p.clientOfflineId,
      payment_no: debit > 0 ? 'Payment' : '',
      description: 'POS Purchase',
      debit,
      credit,
      balance,
    };
  });

  return {
    party_name: opts.supplierName || opts.supplier,
    opening_balance: opening,
    closing_balance: balance,
    balance_label: balance >= 0 ? 'Payable' : 'Advance',
    entries,
    from_date: opts.from_date,
    to_date: opts.to_date,
    source: 'local' as const,
  };
}

export async function saveLocalPurchase(data: {
  supplier: string;
  supplierName?: string;
  items: Array<Record<string, unknown>>;
  discount_amount?: number;
  tax_amount?: number;
  payments?: Array<Record<string, unknown>>;
  notes?: string;
  net_total?: number;
  create_stock_entry?: number;
}): Promise<string> {
  if (!isLocalFirstMode()) throw new Error('Local purchase only in local-first modes');
  const clientOfflineId = newOfflineId('pur');
  const deviceId = await ensureDeviceId();
  const createdAt = new Date().toISOString();
  let itemTotal = 0;
  for (const line of data.items) {
    itemTotal += Number(line.amount) || 0;
  }
  const discount = Number(data.discount_amount) || 0;
  const tax = Number(data.tax_amount) || 0;
  const netTotal = data.net_total ?? itemTotal - discount + tax;
  const paid = (data.payments || []).reduce((s, p) => s + (Number(p.amount) || 0), 0);

  // Increase local stock
  if (data.create_stock_entry !== 0) {
    for (const line of data.items) {
      const itemName = String(line.item || '');
      const variant = line.variant ? String(line.variant) : undefined;
      const qty = Number(line.quantity) || 0;
      const key = stockKey(itemName, variant);
      const row = await companyDb().local_stock.get(key);
      await companyDb().local_stock.put({
        id: key,
        item: itemName,
        variant,
        qty: (row?.qty || 0) + qty,
      });
    }
  }

  await companyDb().local_purchases.put({
    clientOfflineId,
    createdAt,
    supplier: data.supplier,
    supplierName: data.supplierName,
    items: data.items,
    discount_amount: discount,
    tax_amount: tax,
    net_total: netTotal,
    paid_amount: paid,
    notes: data.notes,
  });

  await enqueueSync('purchase_invoice', clientOfflineId, {
    supplier: data.supplier,
    items: data.items,
    item_total: itemTotal,
    discount_amount: discount,
    tax_amount: tax,
    net_total: netTotal,
    payments: data.payments || [],
    notes: data.notes,
    create_stock_entry: data.create_stock_entry ?? 1,
    client_offline_id: clientOfflineId,
    offline_device_id: deviceId,
    invoice_date: createdAt,
  });
  triggerAutoSync();
  return clientOfflineId;
}

export async function saveLocalReturn(data: {
  pos_invoice: string;
  items: Array<Record<string, unknown>>;
  return_method?: string;
  return_bank?: string;
  pos_shift?: string;
}): Promise<string> {
  if (!isLocalFirstMode()) throw new Error('Local return only in local-first modes');
  const clientOfflineId = newOfflineId('ret');
  const deviceId = await ensureDeviceId();
  const createdAt = new Date().toISOString();

  let refund = 0;
  for (const line of data.items) {
    const qty = Number(line.quantity) || 0;
    const rate = Number(line.rate) || 0;
    refund += qty * rate;
    if (String(line.item_type || 'Product') === 'Product') {
      const itemName = String(line.item || '');
      const variant = line.variant ? String(line.variant) : undefined;
      const key = stockKey(itemName, variant);
      const row = await companyDb().local_stock.get(key);
      await companyDb().local_stock.put({
        id: key,
        item: itemName,
        variant,
        qty: (row?.qty || 0) + qty,
      });
    }
  }

  // Try match local sale for rates if missing
  const localSale = await companyDb().local_sales
    .filter((s) => s.offlineInvoiceNo === data.pos_invoice || s.clientOfflineId === data.pos_invoice || s.serverInvoiceName === data.pos_invoice)
    .first();

  await companyDb().local_returns.put({
    clientOfflineId,
    createdAt,
    pos_invoice: data.pos_invoice,
    items: data.items,
    refund_amount: refund,
  });

  const invoiceRef = localSale?.serverInvoiceName || (await resolveServerId(data.pos_invoice)) || data.pos_invoice;

  await enqueueSync('sales_return', clientOfflineId, {
    pos_invoice: invoiceRef,
    items: data.items,
    return_method: data.return_method,
    return_bank: data.return_bank,
    pos_shift: data.pos_shift,
    client_offline_id: clientOfflineId,
    offline_device_id: deviceId,
    _local_invoice: data.pos_invoice,
  });
  triggerAutoSync();
  return clientOfflineId;
}

export async function findLocalInvoiceForReturn(invoiceRef: string) {
  const sale = await companyDb().local_sales
    .filter(
      (s) =>
        s.offlineInvoiceNo === invoiceRef ||
        s.clientOfflineId === invoiceRef ||
        s.serverInvoiceName === invoiceRef,
    )
    .first();
  if (!sale) return null;
  return {
    name: sale.serverInvoiceName || sale.offlineInvoiceNo,
    customer: sale.customer,
    customer_name: sale.customerName,
    items: sale.items,
    net_total: sale.net_total,
    offline: !sale.serverInvoiceName,
  };
}

export async function scanLocalBarcode(code: string): Promise<CatalogItem | null> {
  const items = await listLocalItems({ search: code });
  const exact = items.find((i) => i.barcode === code);
  if (exact) return exact;
  for (const item of items) {
    const v = (item.variants || []).find((x) => (x as { barcode?: string }).barcode === code);
    if (v) return { ...item, /* mark variant */ };
  }
  return items[0] || null;
}
